<template>
  <v-container
    class="py-0 pl-0 pr-0"
    :class="{
      'main-container': !forLiveSearch,
    }"
  >
    <v-row dense row wrap>
      <v-col class="col-12">
        <div style="text-align: center;">
          <v-btn
            block
            outlined
            color="primary"
            class="all_button mb-3"
            @click="handleEnableDisableAllEventFilters"
            >{{
              allMapped
                ? "Disable All Event Filters"
                : "Enable All Event Filters"
            }}</v-btn
          >
        </div>
        <EventPicker
          v-for="topic in topics"
          :topic="topic"
          :key="topic.type"
          :selected-subtopics="getItems(topic)"
          :reset="reset"
          :topic-filter-counts="topicFilterCounts"
          :expanded="expanded.includes(topic.type)"
          :map-all="mapAll"
          @changed="handleChange"
          @changed-live-search="handleChangeLiveSearch"
          @expanded="handleExpanded"
        ></EventPicker>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashOrderBy from "lodash/orderBy";
import lodashRemove from "lodash/remove";
import lodashUniq from "lodash/uniq";
import lodashCloneDeep from "lodash/cloneDeep";

import { getPrimaryTopics, findTopic } from "@/services/events/topics.service";

const EMITTED_ACTIONS = {
  CHANGED: "changed",
  CHANGED_LIVE_SEARCH: "changed-live-search",
  CHANGE_HAS_TOPICS_SELECTED: "change-has-topics-selected",
};

export default {
  name: "EventsPicker",
  components: {
    EventPicker: () => import("@/components/events/EventPicker"),
  },
  props: {
    eventsSelection: {
      type: Object,
      default() {},
    },
    topicFilterCounts: {
      type: Object,
    },
    forLiveSearch: {
      type: Boolean,
      default: false,
    },
    reset: {
      type: String,
    },
    eventType: {
      type: String,
    },
  },
  data: () => ({
    topics: [],
    items: null,
    itemsLiveSearch: null,
    selection: null,
    expanded: [],
    mapAll: null,
  }),
  mounted() {
    this.selection = lodashCloneDeep(this.eventsSelection);
    this.initialize();
  },
  computed: {
    allMapped() {
      let isOneMapped = false;
      for (let i = 0; i < this.topics.length; i++) {
        if (this.topics[i].subtopics && this.topics[i].subtopics.length) {
          for (let j = 0; j < this.topics[i].subtopics.length; j++) {
            if (
              this.itemsLiveSearch.indexOf(this.topics[i].apiName) > -1 ||
              this.itemsLiveSearch.indexOf(
                this.topics[i].subtopics[j].apiName
              ) > -1
            ) {
              isOneMapped = true;
              break;
            }
          }
        } else {
          if (this.itemsLiveSearch.indexOf(this.topics[i].apiName) > -1) {
            isOneMapped = true;
            break;
          }
        }
      }
      return isOneMapped;
    },
    hasTopicsSelected() {
      return this.itemsLiveSearch && this.itemsLiveSearch.length > 0;
    },
  },
  methods: {
    initialize() {
      let topics = [];
      const PRIMARY_TOPICS = getPrimaryTopics(this.eventType);
      const SORTED_TOPICS = lodashOrderBy(PRIMARY_TOPICS, ["order"], ["asc"]);
      SORTED_TOPICS.forEach((topic) => {
        if (this.featureUnlocked(`event-${topic.type}`)) {
          let subtopics = [];
          if (topic.subtopics.length) {
            topic.subtopics.forEach((sTopic) => {
              if (this.featureUnlocked(`event-${sTopic.apiName}`)) {
                subtopics.push(sTopic);
              }
            });
            topic.subtopics = subtopics;
          }

          let selected = false;
          if (this.selection) {
            Object.keys(this.selection).map((key) => {
              if (topic.apiName === this.selection[key].apiName) {
                selected = true;
              }
            });
          }

          topic.selected = selected;
          topic.watchTrip = new Date().toISOString();
          topics.push(topic);
        }
      });
      this.topics = topics;
      this.items = [];
      this.itemsLiveSearch = this.constructItemsLiveSearchFromEventsSelection(
        this.selection
      );
    },
    constructItemsLiveSearchFromEventsSelection(eventsSelection) {
      let res = [];
      for (const prop in eventsSelection) {
        if (Object.prototype.hasOwnProperty.call(eventsSelection, prop)) {
          let cur = eventsSelection[prop];
          if (cur.selected === true) {
            res.push(cur.apiName);
          } else if (cur.subtopics) {
            res = res.concat(cur.subtopics);
          }
        }
      }
      return res;
    },
    getItems(topic) {
      let selected = [];
      if (topic && this.selection) {
        const eventSelection = this.selection[topic.apiName];
        topic.subtopics.forEach((t) => {
          if (
            eventSelection &&
            ((eventSelection.subtopics &&
              eventSelection.subtopics.includes(t.apiName)) ||
              eventSelection.selected)
          ) {
            selected.push(t.apiName);
          }
        });
      }
      return selected;
    },
    handleEnableDisableAllEventFilters() {
      this.mapAll = {
        enabled: !this.allMapped,
        date: new Date().toISOString(),
      };
    },
    handleExpanded(item) {
      if (item.expanded) {
        this.expanded.push(item.topic);
      } else {
        this.expanded = lodashRemove(this.expanded, (check) => {
          return check !== item.topic;
        });
      }
    },
    handleChangeLiveSearch(item) {
      let itemsCopy = JSON.parse(JSON.stringify(this.itemsLiveSearch));

      //clear out existing topic/subtopic
      lodashRemove(itemsCopy, (compare) => {
        return item.topic.apiName === compare;
      });
      if (item.topic.subtopics) {
        for (let i = 0; i < item.topic.subtopics.length; i++) {
          lodashRemove(itemsCopy, (compare) => {
            return item.topic.subtopics[i].apiName === compare;
          });
        }
      }

      if (item.allSubtopicsSelected && item.enabled) {
        itemsCopy.push(item.topic.apiName);
      } else if (item.subtopics && item.subtopics.length > 0) {
        itemsCopy = itemsCopy.concat(item.subtopics);
      } else if (item.enabled === true) {
        itemsCopy.push(item.topic.apiName);
      }
      itemsCopy = lodashUniq(itemsCopy);

      this.itemsLiveSearch = itemsCopy;
      this.$emit(EMITTED_ACTIONS.CHANGED_LIVE_SEARCH, item, itemsCopy);
    },
    handleChange(item) {
      if (item.enabled) {
        if (item.subtopic) {
          this.items = this.items.concat(item.subtopics);
        } else {
          this.items.push(item.topic.type);
        }
      }
      this.items = lodashUniq(this.items);
      this.$emit(EMITTED_ACTIONS.CHANGED, item, this.items);
    },
  },
  watch: {
    eventsSelection: {
      handler() {
        this.selection = lodashCloneDeep(this.eventsSelection);
        //setting itemsLiveSearch by selection so the toggle all button functions correctly
        let itemsLiveSearch = [];
        for (let prop in this.selection) {
          if (Object.prototype.hasOwnProperty.call(this.selection, prop)) {
            let currentSelection = this.selection[prop];
            let currentSelectionTopic = findTopic(
              this.selection[prop].apiName,
              "apiName"
            );
            if (
              (currentSelection.selected && !currentSelection.subtopics) ||
              currentSelection.subtopics.length === 0 ||
              (currentSelectionTopic.subtopics &&
                currentSelection.subtopics.length ===
                  currentSelectionTopic.subtopics.length)
            ) {
              itemsLiveSearch.push(this.selection[prop].apiName);
            } else if (currentSelection.subtopics) {
              itemsLiveSearch = itemsLiveSearch.concat(
                this.selection[prop].subtopics
              );
            }
          }
        }
        this.itemsLiveSearch = itemsLiveSearch;

        if (this.forLiveSearch) {
          this.initialize();
        }
      },
      deep: true,
    },
    hasTopicsSelected: {
      handler() {
        this.$emit(
          EMITTED_ACTIONS.CHANGE_HAS_TOPICS_SELECTED,
          this.hasTopicsSelected
        );
      },
    },
    eventType: {
      handler() {
        if (this.forLiveSearch) {
          this.initialize();
        }
      },
    },
  },
};
</script>

<style scoped>
.main-container {
  height: 90vh;
  overflow-y: auto;
}
.hyp-col-container {
  padding: 12px 12px 0 3px;
  height: 45px;
}
.switch-col-container {
  padding: 12px 12px 0 11px;
  height: 45px;
}
.all_button {
  font-size: 14px;
}
</style>
